/**
 * Utility functions for handling cookies
 */

/**
 * Get a cookie by name
 * @param {string} name - The cookie name
 * @returns {string|null} - The cookie value or null if not found
 */
export const getCookie = (name) => {
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    // Check if this cookie starts with the name we want
    if (cookie.startsWith(name + '=')) {
      return cookie.substring(name.length + 1);
    }
  }
  return null;
};

/**
 * Set a cookie
 * @param {string} name - The cookie name
 * @param {string} value - The cookie value
 * @param {number} days - Days until the cookie expires
 */
export const setCookie = (name, value, days) => {
  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    expires = '; expires=' + date.toUTCString();
  }
  document.cookie = name + '=' + value + expires + '; path=/';
};

/**
 * Remove a cookie
 * @param {string} name - The cookie name
 */
export const removeCookie = (name) => {
  setCookie(name, '', -1);
};

/**
 * Get authentication token from cookie
 * @returns {string|null} - The authentication token or null if not found
 */
export const getAuthToken = () => {
  return getCookie('auth_token');
};

// Call Me specific helpers
const CALL_ME_COOKIE_KEY = 'rnr_call_me_id';

export const getCallMeId = () => getCookie(CALL_ME_COOKIE_KEY);

export const setCallMeId = (id) => setCookie(CALL_ME_COOKIE_KEY, id, 1); // 1 day expiry

export const clearCallMeId = () => removeCookie(CALL_ME_COOKIE_KEY);

// Panic specific helpers
const PANIC_COOKIE_KEY = 'rnr_panic_id';

export const getPanicId = () => getCookie(PANIC_COOKIE_KEY);

export const setPanicId = (id) => setCookie(PANIC_COOKIE_KEY, id, 1); // 1 day expiry

export const clearPanicId = () => removeCookie(PANIC_COOKIE_KEY);

// User details helpers
const USER_DETAILS_COOKIE_KEY = 'rnr_user_details';

export const setUserDetails = (details) => {
  try {
    const value = encodeURIComponent(JSON.stringify(details || {}));
    setCookie(USER_DETAILS_COOKIE_KEY, value, 1); // 1 day expiry
  } catch (e) {
    console.warn('Failed to set user details cookie', e);
  }
};

export const getUserDetails = () => {
  try {
    const raw = getCookie(USER_DETAILS_COOKIE_KEY);
    if (!raw) return null;
    return JSON.parse(decodeURIComponent(raw));
  } catch (e) {
    console.warn('Failed to parse user details cookie', e);
    return null;
  }
};

export const clearUserDetails = () => removeCookie(USER_DETAILS_COOKIE_KEY);

// Accident Report helpers
const ACCIDENT_REPORT_COOKIE_KEY = 'rnr_accident_report';

export const setAccidentReport = (data) => {
  try {
    const value = encodeURIComponent(JSON.stringify(data || null));
    setCookie(ACCIDENT_REPORT_COOKIE_KEY, value, 1); // 1 day expiry
  } catch (e) {
    console.warn('Failed to set accident report cookie', e);
  }
};

export const getAccidentReport = () => {
  try {
    const raw = getCookie(ACCIDENT_REPORT_COOKIE_KEY);
    if (!raw) return null;
    return JSON.parse(decodeURIComponent(raw));
  } catch (e) {
    console.warn('Failed to parse accident report cookie', e);
    return null;
  }
};

export const clearAccidentReport = () => removeCookie(ACCIDENT_REPORT_COOKIE_KEY);
